import type { DecisionGraph } from "@imminently/decisively-core";
import { type GraphNode, type NodeElement, type Trigger, findNode } from "@packages/commons";
import { forEach } from "lodash";
import { validate as uuidValidate } from "uuid";

export interface FlowStep {
  id: string;
  containment: string;
  triggers: FlowTrigger[];
}

export interface FlowTrigger {
  input: string;
  actions: Trigger[];
  required?: boolean;
  decisive?: boolean;
  complex?: boolean;
}

export type TriggerStatus = "requiredDecisive" | "requiredNotDecisive" | "optionalDecisive" | "optionalNotDecisive";

export const getTriggerStatus = (trigger: FlowTrigger): TriggerStatus => {
  if (trigger.required) {
    return trigger.decisive ? "requiredDecisive" : "requiredNotDecisive";
  } else {
    return trigger.decisive ? "optionalDecisive" : "optionalNotDecisive";
  }
};

const findNodeWithDefault: typeof findNode = (graph, documents, descriptionOrId): GraphNode | undefined => {
  if (!descriptionOrId) return undefined as any;

  let node = findNode(graph, documents, descriptionOrId);
  if (!node && !uuidValidate(descriptionOrId)) {
    node = {
      description: descriptionOrId,
    } as GraphNode;
  }
  return node;
};

/*
const convertDecisionFlow = (flow, graph, docName, sectionId, existing) => {
  const rules: any[] = [];
  for (const n of flow.nodes()) {
    const block = flow.node(n);
    switch (block.type) {
      case "trigger": {
        if (!block.triggers) continue;
        // Check if the containment isn't defined elsewhere (which means we can't put it here - so this is an error state)
        let node = findNode(graph, undefined, block.containment);
        if (node) {
          // Check if this is defined in this section - which means we can preserve it
          const conc = findConclusion(node.description, existing);

          if (node.definedIn === docName && conc) {
            // it's defined here - for now I think we don't make any changes and just leave it as is
            rules.push(conc);
          } else {
            //console.log('not found', conc, block.containment, existing, node)
            // It's defined elsewhere - error state
            rules.push({
              type: "p",
              text: `Invalid flow: The rules for ${block.containment} task is defined already in another document or section. Either you need to change the name or bring those rules back into this rule block`,
            });
          }
        } else {
          const conclusion = {
            type: "conclusion",
            text: block.containment,
            sectionId,
            children: [
              {
                type: "operator",
                operator: block.required ? "all" : "any",
                children: block.triggers.map((t) => {
                  const input = findNodeWithDefault(graph, undefined, t.input);
                  if (input) {
                    return {
                      type: "condition",
                      text: block.required ? input.description : `not(${input.description})`,
                    };
                  }
                }),
              },
            ],
          };
          for (const t of block.triggers) {
            for (const out of t.actions) {
              const node = findNodeWithDefault(graph, undefined, out.outcome);
              if (node) {
                conclusion.children[0].children.push({
                  type: "condition",
                  text: block.decisive ? node.description : `${node.description} is known`,
                });
              }
            }
          }

          rules.push(conclusion);
        }
        // We need to add the input attributes, check if it doesn't already exist
        for (const t of block.triggers) {
          node = findNodeWithDefault(graph, undefined, t.input);
          // TODO: we need to check that the definition isn't inside this section (which means we can overwrite/merge)
          if (node && !node.id) {
            // We have to add it
            // Check to see if this trigger is connected to something (which would then be the condition for this)
            const parents = flow.predecessors(n);
            if (parents && parents.length > 0) {
              const conclusion: any = {
                type: "conclusion",
                text: node.description,
                sectionId,
                children: [
                  {
                    type: "operator",
                    operator: "all",
                    children: [],
                  },
                ],
              };
              for (const p of parents) {
                const node = flow.node(p);
                switch (node.type) {
                  case "trigger":
                    conclusion.children[0].children.push({
                      type: "condition",
                      text: findNodeWithDefault(graph, undefined, node.containment)?.description,
                    });
                    break;
                  default:
                    console.log(`Unknown parent trigger ${node.type}`);
                }
              }
              rules.push(conclusion);
            } else {
              rules.push({
                type: "conclusion",
                text: `${node.description} = true`,
                sectionId,
              });
            }
          } else if (node?.definedIn === docName) {
            // Check that to see if the attr is defined within this section, if so we can merge
            // Otherwise it's effectively as if it was defined in another document
            const conc = findConclusion(node?.description, existing);
            if (conc) {
              // It's defined in this section, so we return it as is
              rules.push(conc);
            }
          } // It already exists so we don't need to do anything
        }
        break;
      }
      case "attribute":
        // Ignore: this handled in the trigger
        break;
      default:
        console.log(`Unknown block type in flow: ${block.type}`);
    }
  }
  return rules;
};
*/

const FLOW_LAYOUT_NODESEP = 80;
const FLOW_LAYOUT_RANKSEP = 120;

/** A box in a decision flow: a trigger (keyed by its containment attribute), an attribute, or a mapping. */
export type FlowNodeData = {
  id: string;
  type: "trigger" | "attribute" | "mapping" | string;
  width: number;
  height: number;
  [key: string]: any;
};

/** An edge in a decision flow, from box `v` to box `w`. */
export type FlowEdgeData = {
  v: string;
  w: string;
  id?: string;
  sourceHandle?: string;
  targetHandle?: string;
  label?: string;
  bad?: boolean;
  animated?: boolean;
};

/**
 * A decision flow: the boxes to draw (by id) and the edges between them, laid out left to right. A UI layout, not a
 * rule graph: the rule graph it is made from is the release's DecisionGraph.
 */
export type FlowGraph = {
  rankdir: "LR";
  nodesep: number;
  ranksep: number;
  nodes: Map<string, FlowNodeData>;
  /** By `flowEdgeKey(v, w)`: one edge per pair, as setting an edge again replaces it. */
  edges: Map<string, FlowEdgeData>;
};

export const flowEdgeKey = (v: string, w: string) => `${v}\u0000${w}`;

const setFlowEdge = (flow: FlowGraph, v: string, w: string, data: Omit<FlowEdgeData, "v" | "w"> = {}) => {
  flow.edges.set(flowEdgeKey(v, w), { ...data, v, w });
};

export const isFlowGraph = (graph: unknown): graph is FlowGraph =>
  Boolean(graph) && (graph as FlowGraph).nodes instanceof Map && (graph as FlowGraph).edges instanceof Map;

const emptyFlowGraph = (): FlowGraph => ({
  rankdir: "LR",
  nodesep: FLOW_LAYOUT_NODESEP,
  ranksep: FLOW_LAYOUT_RANKSEP,
  nodes: new Map(),
  edges: new Map(),
});

/**
 * The lowest common ancestors of the search nodes (rule graph ids): the nodes that are an ancestor of every one of
 * them, without a successor that is one too. One id when there is exactly one, otherwise all of them.
 */
const findLCA = (searchNodes: number[], graph: DecisionGraph): number | number[] => {
  // https://www.baeldung.com/cs/lowest-common-ancestor-acyclic-graph
  // Algo
  // From each node mark its ancestors
  // Then determine the intersection of these nodes
  const marks = new Map<number, number>();
  for (const n of searchNodes) {
    const seen = new Set<number>([n]);
    const stack = [n];
    while (stack.length > 0) {
      const node = stack.pop()!;
      for (const p of graph.predecessors(node)) {
        if (seen.has(p)) continue;
        seen.add(p);
        marks.set(p, (marks.get(p) ?? 0) + 1);
        stack.push(p);
      }
    }
  }

  // Now keep only the nodes that are a common ancestor to all these nodes
  const common: number[] = [];
  for (const [id, count] of marks) if (count === searchNodes.length) common.push(id);
  const isCommon = new Set(common);
  // There could be multiple lowest ancestors, so return all of them in that case - the calling algo can determine what to do
  const lowest = common.filter((id) => !graph.successors(id).some((to) => isCommon.has(to)));
  if (lowest.length === 1) return lowest[0];
  else return lowest;
};

/** An attribute's box, sized for its description. */
const attributeBox = (graph: DecisionGraph, id: number): FlowNodeData => {
  const key = graph.deref(id);
  const nodeDescription = (graph.field(id, "description") as string | undefined) || key || "1";
  const descLen = String(nodeDescription).length;
  return {
    type: "attribute",
    width: Math.max(220, Math.min(400, 80 + descLen * 6)),
    height: Math.max(98, (descLen / 32) * 17.5 + 20),
    id: key,
  };
};

export const generateFlowGraph = (graph: DecisionGraph | null | undefined, sectionId?: string, rootNodeId?: string) => {
  const flow = emptyFlowGraph();
  if (!graph) return flow;
  // Find all attribute that have triggers and process them
  for (const n of graph.ids()) {
    const triggers = graph.field(n, "triggers") as any[] | undefined;
    // Only include nodes in this section
    // If rootNodeId is provided, only include nodes that are the root node or reachable from it
    if (!triggers || (sectionId && graph.field(n, "sectionId") !== sectionId)) continue;
    // If rootNodeId is specified, only process flows starting from that node
    if (rootNodeId && (typeof rootNodeId !== "string" || graph.deref(n) !== rootNodeId)) continue;
    const node = graph.node(n)!;
    // It has triggers, which are a core part of the flow, lets add them

    // We need to determine if this already exists, but to do so we need the containment. But to get the containment we need the LCA.
    // Ensure node.id is a valid string before proceeding
    if (!node.id || typeof node.id !== "string") {
      console.log("Skipping node with invalid id", node.id);
      continue;
    }

    const searchKeys = new Set<string>([node.id]);
    const actions: any[] = [];

    for (const t of triggers) {
      // Each trigger t from node.triggers is itself a Trigger object
      // We need to preserve it, but update the outcome ID if we can find the node
      const outcome = findNode(graph, undefined, t.outcome);
      if (outcome?.id && typeof outcome.id === "string") {
        // Update the outcome ID and preserve all other properties
        actions.push({
          ...t,
          outcome: outcome.id,
        });
        searchKeys.add(outcome.id);
      } else {
        // Even if we can't find the outcome node, preserve the trigger as-is
        // This ensures actions and other properties are not lost
        actions.push(t);
        // Still try to add the outcome ID to searchNodes if it exists (even if node not found)
        if (t.outcome) {
          searchKeys.add(t.outcome);
        }
      }
    }
    // a search node not in the graph has no ancestors, so there's no common one
    const searchNodes = [...searchKeys].map((key) => graph.ref(key));
    // Now find the lowest common ancestor of these nodes. This will be the containment node
    const commonParentId = searchNodes.includes(undefined) ? [] : findLCA(searchNodes as number[], graph);
    if (Array.isArray(commonParentId)) {
      if (commonParentId.length === 0) console.log("TODO: handle no common parent");
      else console.log("TODO: handle multiple LCA", commonParentId.map((id) => graph.deref(id)), [...searchKeys]);
      continue;
    }
    // We have a common parent - this is the containment attribute
    const commonParent = graph.deref(commonParentId);
    // Now check if this exists in the flow already
    let element: any = flow.nodes.get(commonParent);
    // If it doesn't exist yet or it was previously not a trigger (most likely an attribute) then reset to look like a blank trigger
    if (!element || element.type !== "trigger")
      element = {
        type: "trigger",
        triggers: [],
      };

    element.triggers.push({
      input: node.id,
      actions: actions,
    });
    element.containment = commonParent;
    // Determine if required/decisive or complex
    // We can only know this if the rules are designed the way we'd expect
    // Otherwise complex
    const containment = graph.node(commonParentId);

    if (!containment) {
      console.log("TODO: handle containment not found", commonParent);
      continue;
    }

    if (containment.conditions) {
      const traverse = (attr: string, el: Partial<NodeElement>, parent?: any) => {
        if (el.type === "attribute" && el.attributeId === attr) return parent;
        else if (el.elements) {
          let outcome: any;
          forEach(el.elements, (subEl) => {
            const sub = traverse(attr, subEl, el);
            if (sub) {
              // Send the parent
              outcome = sub;
              return false;
            }
          });
          return outcome;
        }
      };

      for (const t of element.triggers) {
        t.required = false;
        t.decisive = false;
        t.complex = false;

        // Check if the input is required or not
        // Find the definition of this input within the conditions
        const parentRule = traverse(t.input, { elements: containment.conditions });

        if (parentRule && parentRule.type === "not") t.required = false;
        else if (parentRule && (parentRule.type === "or" || parentRule.type === "and")) t.required = true;
        else t.complex = true;

        // Now to determine decisive we need to check the outcomes, which there could be multiple of
        for (const a of t.actions) {
          const parentRule = traverse(a.outcome, { elements: containment.conditions });
          if (parentRule && parentRule.type === "known") t.decisive = false;
          else if (parentRule && (parentRule.type === "or" || parentRule.type === "and")) t.decisive = true;
          else t.complex = true;
        }
      }
    }

    element.id = element.containment;
    element.height = 100 + element.triggers.length * 200;
    element.width = 320;
    flow.nodes.set(element.containment, element);
    // We also need to add any children to the 'input' attributes
    for (const t of element.triggers) {
      const input = graph.ref(t.input);
      if (input === undefined) continue;
      for (const c of graph.successors(input)) {
        const childKey = graph.deref(c);
        const existingFlowNode: any = flow.nodes.get(childKey);
        let id: string | undefined;
        if (existingFlowNode) {
          // It already exists - do nothing?
          if (existingFlowNode.type === "attribute") id = existingFlowNode.id;
          else if (existingFlowNode.type === "trigger") id = existingFlowNode.containment;
          else console.log("unknown node type in edge connection", existingFlowNode);
        } else {
          // the attribute's box, sized for its description
          flow.nodes.set(childKey, attributeBox(graph, c));
          id = childKey;
        }
        // Ensure both IDs are valid strings before creating the edge
        // Also ensure t.input is a valid string for the edge targetHandle
        if (id && typeof id === "string" && typeof element.containment === "string" && typeof t.input === "string") {
          setFlowEdge(flow, id, element.containment, { targetHandle: t.input });
        } else {
          console.log("Skipping edge creation due to invalid IDs or input", {
            id,
            containment: element.containment,
            input: t.input,
          });
        }
      }
    }
  }

  // Validate node data is valid before returning
  for (const [nodeId, nodeData] of flow.nodes) {
    // Ensure all string properties are actually strings
    if (nodeData.id !== undefined && typeof nodeData.id !== "string") {
      console.error("Invalid node.id type:", nodeData.id, "for node:", nodeId);
      nodeData.id = String(nodeData.id || nodeId);
    }
    if (nodeData.type !== undefined && typeof nodeData.type !== "string") {
      console.error("Invalid node.type:", nodeData.type, "for node:", nodeId);
      nodeData.type = String(nodeData.type || "node");
    }
    if (nodeData.containment !== undefined && typeof nodeData.containment !== "string") {
      console.error("Invalid node.containment:", nodeData.containment, "for node:", nodeId);
      nodeData.containment = String(nodeData.containment || nodeId);
    }
  }

  return flow;
};

export interface ReleaseMapping {
  id?: string;
  name: string;
  mode?: "json" | "csv";
  data?: Record<string, unknown>;
}

export interface ReleaseInterview {
  id: string;
  goal?: string;
  name?: string;
}

export interface ReleaseWithMapping {
  mapping?: ReleaseMapping[];
  interviews?: ReleaseInterview[];
}

/** Recursively collect all string keys from a data object (objects and arrays, all depths). */
function collectDataKeys(obj: unknown): Set<string> {
  const keys = new Set<string>();
  function visit(val: unknown): void {
    if (val === null || val === undefined) {
      return;
    }
    if (Array.isArray(val)) {
      for (const item of val) {
        visit(item);
      }
      return;
    }
    if (typeof val === "object") {
      for (const k of Object.keys(val)) {
        keys.add(k);
        visit((val as Record<string, unknown>)[k]);
      }
    }
  }
  visit(obj);
  return keys;
}

/** A node in the rule graph whose description matches the given key (exact match): its id and key. */
function findNodeByDescription(parsedGraph: DecisionGraph, key: string): { id: number; key: string } | undefined {
  for (const id of parsedGraph.nodesByDescription(key)) {
    if (parsedGraph.field(id, "description") !== key) continue;
    const nodeId = parsedGraph.field(id, "id");
    if (nodeId) return { id, key: String(nodeId) };
  }
  return undefined;
}

/** Enriches the flow graph with mapping nodes and mapping→attribute edges from mapping.data. */
export const enrichFlowGraphWithMappingNodes = (
  flowGraph: FlowGraph,
  release: ReleaseWithMapping | null | undefined,
  parsedGraph: DecisionGraph | null | undefined,
): FlowGraph => {
  if (!release) {
    return flowGraph;
  }
  const hasMapping = Boolean(release.mapping?.length);
  const hasInterviews = Boolean(release.interviews?.length);
  if (!hasMapping && !hasInterviews) {
    return flowGraph;
  }

  const mappingById = new Map<string, ReleaseMapping>();
  for (const m of release.mapping ?? []) {
    const id = m.id ?? (m as any).name;
    if (id) {
      mappingById.set(id, m);
    }
  }

  const interviewById = new Map<string, ReleaseInterview>();
  if (release.interviews?.length) {
    for (const i of release.interviews) {
      if (i.id) {
        interviewById.set(i.id, i);
      }
    }
  }

  const enriched: FlowGraph = {
    rankdir: flowGraph.rankdir ?? "LR",
    nodesep: flowGraph.nodesep ?? FLOW_LAYOUT_NODESEP,
    ranksep: flowGraph.ranksep ?? FLOW_LAYOUT_RANKSEP,
    nodes: new Map(),
    edges: new Map(),
  };

  for (const [nodeId, nodeData] of flowGraph.nodes) {
    enriched.nodes.set(nodeId, { ...nodeData });
  }

  for (const [key, edge] of flowGraph.edges) {
    enriched.edges.set(key, { ...edge });
  }

  const existingFlowNodeIds = new Set(flowGraph.nodes.keys());

  for (const nodeId of flowGraph.nodes.keys()) {
    const nodeData = enriched.nodes.get(nodeId) as any;
    if (nodeData?.type !== "trigger" || !nodeData.triggers?.length) {
      continue;
    }

    const mappingIdsUsed = new Set<string>();
    for (const trigger of nodeData.triggers) {
      for (const action of trigger.actions ?? []) {
        const responseMappingId = action?.assignmentIntegration?.responseMapping;
        if (responseMappingId && mappingById.has(responseMappingId)) {
          mappingIdsUsed.add(responseMappingId);
        }
      }
    }

    for (const mappingId of mappingIdsUsed) {
      const mapping = mappingById.get(mappingId);
      if (!mapping) {
        continue;
      }
      const mappingNodeId = `mapping:${mappingId}`;
      if (!enriched.nodes.has(mappingNodeId)) {
        enriched.nodes.set(mappingNodeId, {
          type: "mapping",
          id: mappingNodeId,
          mappingId,
          name: mapping.name,
          mode: mapping.mode ?? "json",
          height: 88,
          width: 220,
        });
      }
    }

    // Per-action edges: shared handle for task actions (mapping + interview goal)
    nodeData.triggers.forEach((trigger: { input: string; actions?: any[] }) => {
      (trigger.actions ?? []).forEach((action: any, actionIndex: number) => {
        if (action?.type !== "task") {
          return;
        }
        const handleId = `task-${nodeId}-${trigger.input}-${actionIndex}`;
        const responseMappingId = action?.assignmentIntegration?.responseMapping;
        if (responseMappingId && mappingById.has(responseMappingId)) {
          const mappingNodeId = `mapping:${responseMappingId}`;
          setFlowEdge(enriched, nodeId, mappingNodeId, {
            id: `responseMapping:${nodeId}:${responseMappingId}:${actionIndex}`,
            sourceHandle: handleId,
            targetHandle: "mapping",
            label: "Uses",
          });
        }
        const interviewId = action?.interview;
        if (interviewId && interviewById.has(interviewId) && parsedGraph) {
          const interview = interviewById.get(interviewId)!;
          const goalNodeId = interview.goal;
          const goal = goalNodeId ? parsedGraph.ref(goalNodeId) : undefined;
          if (goalNodeId && goal !== undefined && parsedGraph.isListed(goal)) {
            if (!enriched.nodes.has(goalNodeId)) {
              enriched.nodes.set(goalNodeId, { ...attributeBox(parsedGraph, goal), id: goalNodeId });
            }
            setFlowEdge(enriched, nodeId, goalNodeId, {
              id: `interviewGoal:${nodeId}:${interviewId}:${actionIndex}`,
              sourceHandle: handleId,
              label: "Interview Goal",
            });
          }
        }
      });
    });
  }

  // Track which trigger nodes use which mappings and their input nodes
  const triggerMappingInfo: Array<{
    triggerNodeId: string;
    inputNodeIds: string[];
    mappingIds: string[];
  }> = [];

  for (const [nodeId, nodeData] of flowGraph.nodes) {
    if (nodeData?.type !== "trigger" || !nodeData.triggers?.length) {
      continue;
    }
    const inputNodeIds: string[] = [];
    const mappingIds: string[] = [];
    for (const trigger of nodeData.triggers) {
      if (trigger.input) {
        inputNodeIds.push(trigger.input);
      }
      for (const action of trigger.actions ?? []) {
        const responseMappingId = action?.assignmentIntegration?.responseMapping;
        if (responseMappingId && mappingById.has(responseMappingId)) {
          mappingIds.push(responseMappingId);
        }
      }
    }
    if (inputNodeIds.length > 0 && mappingIds.length > 0) {
      triggerMappingInfo.push({ triggerNodeId: nodeId, inputNodeIds, mappingIds });
    }
  }
  // Collect mapping output attribute node IDs per mapping
  const mappingOutputNodes = new Map<string, Set<string>>();

  if (parsedGraph) {
    for (const [nodeId, nodeData] of [...enriched.nodes]) {
      if (nodeData?.type !== "mapping" || !nodeData.mappingId) {
        continue;
      }
      const mapping = mappingById.get(nodeData.mappingId) as ReleaseMapping | undefined;
      if (!mapping?.data || typeof mapping.data !== "object") {
        continue;
      }
      const dataKeys = collectDataKeys(mapping.data);
      const outputIds = new Set<string>();
      for (const key of dataKeys) {
        const matched = findNodeByDescription(parsedGraph, key);
        if (!matched) {
          continue;
        }
        const attrNodeId = matched.key;
        outputIds.add(attrNodeId);
        const alreadyInFlow = existingFlowNodeIds.has(attrNodeId);
        if (!enriched.nodes.has(attrNodeId)) {
          enriched.nodes.set(attrNodeId, { ...attributeBox(parsedGraph, matched.id), id: attrNodeId });
        }
        setFlowEdge(enriched, nodeId, attrNodeId, {
          id: `mappingData:${key}`,
          sourceHandle: "data",
          bad: alreadyInFlow,
          label: "Updates",
        });
      }
      mappingOutputNodes.set(nodeData.mappingId, outputIds);
    }

    // how far each node is from a trigger input, along successors (made once per input)
    const distancesFrom = new Map<string, Map<number, number> | undefined>();
    const isReachableViaSuccessors = (sourceNodeId: string, targetNodeId: string, maxDepth = 100): boolean => {
      if (sourceNodeId === targetNodeId) return true;
      if (!distancesFrom.has(sourceNodeId)) {
        const source = parsedGraph.ref(sourceNodeId);
        distancesFrom.set(sourceNodeId, source === undefined ? undefined : parsedGraph.distances(source, "successors"));
      }
      const target = parsedGraph.ref(targetNodeId);
      const distance = target === undefined ? undefined : distancesFrom.get(sourceNodeId)?.get(target);
      // the walk went on from nodes up to maxDepth steps away
      return distance !== undefined && distance > 0 && distance <= maxDepth + 1;
    };

    // Check for connections between trigger inputs and mapping outputs via the parsed graph
    for (const { triggerNodeId, inputNodeIds, mappingIds } of triggerMappingInfo) {
      const allOutputNodeIds = new Set<string>();
      for (const mid of mappingIds) {
        const outputs = mappingOutputNodes.get(mid);
        if (outputs) {
          for (const o of outputs) {
            allOutputNodeIds.add(o);
          }
        }
      }
      // Find child nodes of the trigger: nodes that have an edge INTO the trigger with targetHandle = inputNodeId
      // (the flow graph shows these children connecting to the trigger, not the input node itself)
      const inputToChildNodes = new Map<string, string[]>();
      for (const edge of enriched.edges.values()) {
        if (edge.w !== triggerNodeId) continue;
        const targetHandle = edge.targetHandle;
        if (targetHandle && inputNodeIds.includes(targetHandle)) {
          const childNodeId = edge.v;
          if (!inputToChildNodes.has(targetHandle)) {
            inputToChildNodes.set(targetHandle, []);
          }
          inputToChildNodes.get(targetHandle)!.push(childNodeId);
        }
      }

      // For each input node, check if any output node is reachable via successors; if so, add warning edge
      // from mapping output (right) to child/input node (left) so the line comes out of the right node and into the left
      for (const inputNodeId of inputNodeIds) {
        const childNodeIds = inputToChildNodes.get(inputNodeId) ?? [];
        for (const outputNodeId of allOutputNodeIds) {
          if (!isReachableViaSuccessors(inputNodeId, outputNodeId)) {
            continue;
          }
          const targetNodeIds = childNodeIds.length > 0 ? childNodeIds : [inputNodeId];
          for (const targetNodeId of targetNodeIds) {
            if (!enriched.nodes.has(targetNodeId)) {
              continue;
            }
            const edgeKey = `warning:${outputNodeId}:${targetNodeId}`;
            if (enriched.edges.has(flowEdgeKey(outputNodeId, targetNodeId))) {
              continue;
            }
            setFlowEdge(enriched, outputNodeId, targetNodeId, {
              id: edgeKey,
              bad: true,
              label: "Warning: Connected",
            });
          }
        }
      }
    }
  }

  return enriched;
};

/*const findConclusion = (concText, rules) => {
  let conc;
  const traverse = (el) => {
    if (el.type === "conclusion" && el.text.includes(concText)) {
      conc = el;
    } else if (el.children) {
      for (const c of el.children) {
        traverse(c);
      }
    }
  };
  if (rules && Array.isArray(rules)) {
    for (const r of rules) {
      traverse(r);
    }
  }
  return conc;
};*/
